import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { IUser } from "./useUsers";
import { useCurrentLocation } from "./useCurrentLocation";

interface ILotteryDetails {
  _id: string;
  en_title: string;
  nl_title: string;
  isFirstTicketDrop?: boolean;
}

export interface IDrop {
  _id: string;
  user: string | IUser;
  statusCode: number;
  statusMessage: string;
  width: number;
  length: number;
  height: number;
  dimWt: string;
  dimUnit: string;
  weightUnit: string;
  realVolume: string;
  weight: string;
  unitId: string;
  branchId: string;
  barcode: string;
  barcodeType: string;
  barcodeSource: string;
  registeredDate: Date;
  registeredTime: string;
  measurementId: string;
  imageBase64: string;
  image: string;
  serialNumber: string;
  isRegular: number;
  operator: string;
  gate: number;
  OCRText: string[];
  AIProcessorResult: string;
  totalPrice: number;
  marketPrice: string;
  location: string;
  joinedLottery?: ILotteryDetails[];
  rejectedLottery?: ILotteryDetails[];
  createdAt: Date;
  updatedAt: Date;
}

interface IStore {
  /** State */
  beevisionDrops: IDrop[];
  hasNextPage: boolean;
  hasNextUserBeevisionDrops: boolean;
  userBeevisionDrops: IDrop[];
  oneBeevisionDrop: IDrop | null;
  deletedBeevisionDrop: string | null;
  /** Callbacks */
  downloadBeevisionDropsInCSV: () => void;
  getBeevisionDrops: (page?: number, limit?: number) => void;
  getUserBeevisionDrops: (
    userId: string,
    page?: number,
    limit?: number
  ) => void;
  getOneBeevisionDrop: (dropId: string) => void;
  deleteBeevisionDrop: (dropId: string, userId?: string) => void;
  /** Clear callbacks */
  clearUserBeevisionDrops: () => void;
  clearDeletedBeevisionDrop: () => void;
}

export const useBeevisionDrops = create<IStore>()((set, get) => ({
  /** State */
  beevisionDrops: [],
  hasNextPage: false,
  hasNextUserBeevisionDrops: false,
  userBeevisionDrops: [],
  oneBeevisionDrop: null,
  deletedBeevisionDrop: null,
  /** Callbacks */
  downloadBeevisionDropsInCSV: async () => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `beevision-drops/csv?location=${currentLocation}`
      );
      if (response?.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "beevision_drops.csv");
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      LOG.error("Error on downloading Beevision drops in CSV", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getBeevisionDrops: async (page = 1, limit = 20) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `beevision-drops?page=${page}&limit=${limit}&location=${currentLocation}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          beevisionDrops:
            page === 1
              ? response?.data
              : [...state.beevisionDrops, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getting Beevision drops", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getUserBeevisionDrops: async (userId, page = 1, limit = 10) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `beevision-drops/user/${userId}?page=${page}&limit=${limit}&location=${currentLocation}`
      );
      if (response?.data) {
        set((state) => ({
          hasNextUserBeevisionDrops: response?.data?.length === limit,
          userBeevisionDrops:
            page === 1
              ? response?.data
              : [...state.userBeevisionDrops, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getting user Beevision drops", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneBeevisionDrop: async (dropId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`beevision-drops/${dropId}`);
      if (response?.data) {
        set({ oneBeevisionDrop: response?.data });
      }
    } catch (error) {
      LOG.error("Error on getting one Beevision drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteBeevisionDrop: async (dropId, userId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`/beevision-drops/${dropId}`);
      if (response?.data) {
        set((state) => ({
          beevisionDrops: state.beevisionDrops.filter(
            (drop) => drop._id !== dropId
          ),
          deletedBeevisionDrop: dropId,
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting a Beevision drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      if (userId) {
        get().getUserBeevisionDrops(userId);
      }
    }
  },
  /** Clear callbacks */
  clearUserBeevisionDrops: () => {
    set({ userBeevisionDrops: [] });
  },
  clearDeletedBeevisionDrop: () => {
    set({ deletedBeevisionDrop: null });
  },
}));
